import { formatDate } from '@angular/common';
import { EventEmitter, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, startWith, tap } from 'rxjs/operators';
import { PlanningRepetition } from 'src/app/classes/planning-repetition';
import { PlanningHasEntity } from '../../classes/planning-has-entity.class';
import { Planning } from '../../classes/planning.class';
import { PlanningAsfaltteam } from '../../classes/planningasfaltteam.class';
import { PlanningCutter } from '../../classes/planningcutter.class';
import { PlanningPreparationteam } from '../../classes/planningpreparationteam.class';
import { PlanningProjectteam } from '../../classes/planningprojectteam.class';
import { Utils } from '../../utils.class';
import { ApiService } from '../api/api.service';
import { EntitiesService } from '../entities/entities.service';
import { EntityTypeCode } from '../entities/entity-type.class';
import { HiringService } from '../hiring.service';
import { LoggerService } from '../logger.service';
import { MaterialService } from '../material/material.service';
import { RealtimeType } from '../realtime/realtime-type.enum';
import { RealtimeService } from '../realtime/realtime.service';
import { UserService } from '../user/user.service';
import { WebsocketService } from '../websocket/websocket.service';
import { StatusService } from './status.service';

export class TonsForDate {
    constructor(
        public date: Date,
        public tons: TonsPerPeriod[]
    ) {}
}

export class TonsPerPeriod {
    constructor(public delivery: number) {}
}

export class TrucksForDate {
    public available: number;

    constructor(
        public date: Date,
        public trucks: number[]
    ) {}
}

@Injectable({
    providedIn: 'root',
})
export class PlanningService extends RealtimeService<Planning> {
    public mouseEnterPlanning = new EventEmitter<Planning>();
    public mouseLeavePlanning = new EventEmitter<Planning>();
    public planningEmitters = new Map<Planning, EventEmitter<Planning>>();

    status$ = this.statusService.getList('planning').pipe(shareReplay(1));
    materials$ = this.materialService.getList(true).pipe(shareReplay(1));

    constructor(
        protected apiService: ApiService,
        private loggerService: LoggerService,
        private entitiesService: EntitiesService,
        private hiringService: HiringService,
        private userService: UserService,
        private statusService: StatusService,
        private materialService: MaterialService,
        protected websocketService: WebsocketService
    ) {
        super(websocketService, RealtimeType.planning);
    }

    public getPlanningFull(planningId: number, withTrashed?: boolean) {
        return this.apiService.getCallData$<Planning>(`planning/${planningId}`, { withTrashed }).pipe(
            tap(planning => {
                planning.mainPlanning = Utils.mainPlanning(planning);
            })
        );
    }

    public deletePlanning(planningId: number) {
        return this.apiService.deleteCall<boolean>(`planning/${planningId}`);
    }

    public deleteRepetitions(fromPlanningId: number) {
        return this.apiService.deleteCall<boolean>(`planning/repetition/${fromPlanningId}`);
    }

    public getFile(planningFileId: number) {
        return this.apiService.getBlobCall(`planning/file/${planningFileId}`);
    }

    public newAsfaltTeamPlanning() {
        const planning = new Planning();
        planning.planning_asfaltteam = new PlanningAsfaltteam();
        planning.planning_asfaltteam.entitytype_key = EntityTypeCode.AsfaltTeam;
        planning.asphalt_list = [];
        planning.planning_cutters = [];
        planning.planning_wipetrucks = [];
        planning.planning_sets = [];
        return planning;
    }

    public newProjectteamPlanning() {
        const planning = new Planning();
        planning.planning_projectteam = new PlanningProjectteam();
        planning.planning_projectteam.entitytype_key = EntityTypeCode.ProjectTeam;
        planning.asphalt_list = [];
        planning.planning_cutters = [];
        planning.planning_wipetrucks = [];
        planning.planning_sets = [];
        return planning;
    }

    public searchGlobal(search: string) {
        return this.apiService.getCallData$<Planning[]>(`planning/search`, { search });
    }

    public newPreparationteamPlanning() {
        const planning = new Planning();
        planning.planning_preparationteam = new PlanningPreparationteam();
        planning.planning_preparationteam.entitytype_key = EntityTypeCode.PreparationTeam;
        planning.planning_cutters = [];
        planning.planning_wipetrucks = [];
        return planning;
    }

    public newCutterPlanning() {
        const planning = new Planning();
        planning.planning_cutters = [new PlanningCutter()];
        planning.planning_cutters[0].entitytype_key = EntityTypeCode.Cutter;
        planning.planning_wipetrucks = [];
        return planning;
    }

    public newWipetruckPlanning() {
        const planning = new Planning();
        planning.planning_wipetrucks = [new PlanningHasEntity()];
        planning.planning_wipetrucks[0].entitytype_key = EntityTypeCode.Wipetruck;
        return planning;
    }

    public itemInDaterange(planning: Planning, fromDate: Date, toDate: Date) {
        return Utils.planningInDaterange(planning, fromDate, toDate);
    }

    public getCurrentPlannings(): readonly Planning[] {
        return this.getItems() ?? [];
    }

    public newPlanningByType(type: string): Planning {
        if (type === EntityTypeCode.AsfaltTeam) {
            return this.newAsfaltTeamPlanning();
        }
        if (type === EntityTypeCode.ProjectTeam) {
            return this.newProjectteamPlanning();
        }
        if (type === EntityTypeCode.PreparationTeam) {
            return this.newPreparationteamPlanning();
        }
        if (type === EntityTypeCode.Cutter) {
            return this.newCutterPlanning();
        }
        if (type === EntityTypeCode.Wipetruck) {
            return this.newWipetruckPlanning();
        }
    }

    public getMainPlanning(planning: Planning): PlanningHasEntity {
        const planningHas = Utils.mainPlanning(planning);
        planningHas._isMainPlanning = true;
        return planningHas;
    }

    public getHiringNames(entityId: number) {
        return this.apiService.getCall$<string[]>(`planning/hiring-names/${entityId}`);
    }

    public getHiringNamesWithEntityId() {
        return this.apiService.getCall$<
            {
                name: string;
                entity_id: number;
            }[]
        >(`planning/hiring-names`);
    }

    public generatePdf(date: Date) {
        const dateString = formatDate(date, 'yyyy-MM-dd', 'nl');
        return this.apiService.getBlobCall(`planning/pdf/${dateString}`);
    }

    public generateTransportPdf(date: Date) {
        const dateString = formatDate(date, 'yyyy-MM-dd', 'nl');
        return this.apiService.getBlobCall(`planning/transport-pdf/${dateString}`);
    }

    public generateEmployeePdf(week: number, year: number) {
        return this.apiService.getBlobCall(`planning/employee-pdf/${week}/${year}`);
    }

    public saveOld(planning: Planning): Promise<Planning> {
        const startTime = Date.now();
        return this.apiService.postCall<Planning>(this.type, planning).then(
            result => {
                const duration = Date.now() - startTime;
                this.loggerService.logError(`Post planning ${planning.id} took ${duration} ms`);
                return result;
            },
            error => {
                const duration = Date.now() - startTime;
                this.loggerService.logError(`FAILED Post planning ${planning.id} took ${duration} ms`);
                throw error;
            }
        );
    }

    public save(planning: Planning) {
        return this.apiService.postCall$<{
            planning: Planning;
        }>(`${this.type}/save`, planning);
    }

    public saveRepetition(planning: PlanningRepetition, repetitionId?: number) {
        if (repetitionId) {
            // Update
            return this.apiService.postCall$<Planning>(`${this.type}/repetition/${repetitionId}`, planning);
        }

        // Create
        return this.apiService.postCall$<Planning>(`${this.type}/repetition`, planning);
    }

    public getPlanningsByRepetition(repetitionId: number) {
        return this.apiService.getCallData$<{ id: number; begindate: string; entitytype_key: string }[]>(
            `${this.type}/repetition/${repetitionId}`
        );
    }

    public saveMultiple(plannings: any, planningId: number) {
        return this.apiService.postCall$<boolean>(this.type + '/multi', { planningHas: plannings, planningId });
    }

    public saveSort(plannings: PlanningHasEntity[]) {
        return this.apiService.postCall$<boolean>(this.type + '/save-sort', {
            planningHas: plannings.map(p => {
                return {
                    id: p.id,
                    order: p.order,
                };
            }),
        });
    }

    public saveSingleOld(planning: any): Promise<PlanningHasEntity> {
        return this.apiService.postCall(this.type + '/single', { planning });
    }

    public saveSingle(planning: any) {
        return this.apiService.postCall$(this.type + '/single', { planning });
    }

    public deleteSingle(planning: any): Promise<Planning> {
        return this.apiService.deleteCall(this.type + '/single', planning);
    }

    public getProjectmangerReport() {
        return this.apiService.getCall(this.type + '/projectmanager');
    }

    public checkEntityAvailability(
        entityId: number,
        planningId: number,
        beginDate: Date,
        endDate: Date,
        skipUnavailable = false
    ): Observable<boolean> {
        return this.apiService.postCallObserver(`${this.type}/check`, {
            entityId,
            planningId,
            beginDate,
            endDate,
            skipUnavailable,
        });
    }

    applyAllEntitiesToPlanningHas(list: Planning[]) {
        list.filter(p => !!p && !p?.deleted).forEach(planning => {
            planning.mainPlanning = Utils.mainPlanning(planning);
            const allEntities = Utils.planningAllEntities(planning);
            planning.planning_has = planning.planning_has ?? [];
            allEntities.forEach(allEntity => {
                if (!planning.planning_has.find(eph => eph === allEntity)) {
                    planning.planning_has = planning.planning_has.filter(eph => eph.id !== allEntity.id);
                    planning.planning_has.push(allEntity);
                }
            });
        });
    }

    public getListByStatus(status: string) {
        return combineLatest([
            this.getList(),
            this.entitiesService.getMap(),
            this.userService.getList(true, true),
        ]).pipe(
            map(([list, entitiesMap, users]) => {
                this.applyAllEntitiesToPlanningHas(list);
                list.forEach(pl => {
                    pl.mainPlanning.entity = entitiesMap.get(pl.mainPlanning.entity_id);
                    pl.performer_user = users.find(u => u.id === pl.performer);
                    pl.projectmanager_user = users.find(u => u.id === pl.projectmanager);
                });
                return list.filter(p => p.status_key === status);
            })
        );
    }

    public getSingle(planningId: number, withTrashed = false): Observable<Planning> {
        const planning$ = withTrashed ? this.getPlanningFull(planningId, withTrashed) : super.getSingle(planningId);
        return combineLatest([
            planning$,
            this.entitiesService.getMap(),
            this.userService.getList(true, true),
            this.materials$,
        ]).pipe(
            map(([planning, entitiesMap, users, materials]) => {
                if (planning.deleted) {
                    return planning;
                }

                this.applyAllEntitiesToPlanningHas([planning]);

                planning.mainPlanning.entity = entitiesMap.get(planning.mainPlanning.entity_id);

                planning.performer_user = users.find(u => u.id === planning.performer);

                planning.projectmanager_user = users.find(u => u.id === planning.projectmanager);

                planning.planning_materials = planning.planning_materials
                    ?.map(pm => {
                        const planningRawMaterialsIds =
                            pm.planning_raw_materials?.map(prm => prm.raw_material_id) ?? [];

                        const material = structuredClone(materials.find(m => m.id === pm.material_id));
                        if (!material) {
                            console.error(`Material ${pm.material_id} not found`, pm);
                            return null;
                        }

                        material.raw_materials = material.raw_materials?.filter(rm =>
                            planningRawMaterialsIds.includes(rm.id)
                        );

                        return { ...pm, material };
                    })
                    .filter(p => !!p);

                return planning;
            })
        );
    }

    public getFilteredList(fromDate: Date, toDate: Date) {
        return combineLatest([
            super.getFilteredList(fromDate, toDate),
            this.entitiesService.getMap(),
            this.hiringService.getList(),
            this.userService.getList(true, true),
            this.materials$,
            this.status$,
        ]).pipe(
            map(([list, entitiesMap, hirings, users, materials, statusList]) => {
                this.applyAllEntitiesToPlanningHas(list);
                list.forEach(pl => {
                    pl.status = statusList.find(s => s.key === pl.status_key);
                    pl.mainPlanning.entity = entitiesMap.get(pl.mainPlanning.entity_id);
                    pl.user_planning?.forEach(up => {
                        up.user = users.find(u => u.id === up.user_id);
                    });

                    pl.planning_materials = pl.planning_materials
                        ?.map(pm => {
                            const planningRawMaterialsIds =
                                pm.planning_raw_materials?.map(prm => prm.raw_material_id) ?? [];

                            const material = structuredClone(materials.find(m => m.id === pm.material_id));
                            if (!material) {
                                console.error(`Material ${pm.material_id} not found`, pm);
                                return null;
                            }
                            pm.material = material;

                            pm.material.raw_materials = pm.material?.raw_materials?.filter(rm =>
                                planningRawMaterialsIds.includes(rm.id)
                            );

                            return pm;
                        })
                        .filter(p => !!p);

                    pl.performer_user = users.find(u => u.id === pl.performer);
                    pl.projectmanager_user = users.find(u => u.id === pl.projectmanager);
                    pl.planning_has.forEach(phe => {
                        if (!!phe.hiring_id) {
                            const hiring = hirings.find(h => h.id === phe.hiring_id);
                            if (hiring) {
                                phe.hiring = structuredClone(hiring);
                            }
                        }
                        phe.driver_user = users.find(u => u.id === phe.driver_user_id);
                        phe.entity = entitiesMap.get(phe.entity_id);
                    });
                });

                return list;
            })
        );
    }

    public getTons(fromDate: Date, toDate: Date, specificDate: Date): Observable<TonsForDate> {
        return new Observable<TonsForDate>(observer => {
            this.getFilteredList(fromDate, toDate).subscribe(planning => {
                const countDate = new Date(fromDate);
                let tonDates: TonsForDate[] = [];
                while (countDate.getTime() < toDate.getTime()) {
                    const tons = [
                        new TonsPerPeriod(Utils.getDeliveryTons(planning, countDate, 6, 18)),
                        new TonsPerPeriod(Utils.getDeliveryTons(planning, countDate, 18, 6)),
                    ];
                    tonDates.push(new TonsForDate(new Date(countDate), tons));
                    countDate.setDate(countDate.getDate() + 1);
                }

                observer.next(tonDates.find(p => p.date.getDate() === specificDate.getDate()));
            });
        });
    }

    public getCountOfUsedTrucks(fromDate: Date, toDate: Date, specificDate: Date): Observable<TrucksForDate> {
        return this.getFilteredList(fromDate, toDate).pipe(
            map(planning => {
                return new TrucksForDate(new Date(specificDate), [
                    Utils.getCountOfUsedTrucksForTime(planning, specificDate, 0, 6),
                    Utils.getCountOfUsedTrucksForTime(planning, specificDate, 6, 12),
                    Utils.getCountOfUsedTrucksForTime(planning, specificDate, 12, 18),
                    Utils.getCountOfUsedTrucksForTime(planning, specificDate, 18, 24),
                ]);
            })
        );
    }

    public getMaxUsedTrucksForRange(
        fromDate: Date,
        toDate: Date,
        skipPlanningId?: number,
        appendPlanning?: Planning
    ): Observable<number> {
        const objectEmitter = this.updatePlanning(appendPlanning).pipe(startWith(appendPlanning));

        return combineLatest([this.getFilteredList(fromDate, toDate), objectEmitter]).pipe(
            map(([planning]) => {
                return Utils.getCountOfUsedTrucks(planning, fromDate, toDate, skipPlanningId, appendPlanning);
            })
        );
    }

    public updatePlanning(planning: Planning) {
        if (!this.planningEmitters.has(planning)) {
            this.planningEmitters.set(planning, new EventEmitter<Planning>());
        }

        this.planningEmitters.get(planning).emit(planning);

        return this.planningEmitters.get(planning);
    }

    public validateUser(
        planningList: Planning[],
        userId: number,
        begindate: Date,
        enddate: Date,
        oldUserPlanningId?: number
    ) {
        const userPlanningValid =
            planningList
                .map(pl => pl.user_planning)
                .flat()
                .filter(userPlanning => {
                    if (
                        userPlanning.user_id === userId &&
                        (!oldUserPlanningId || oldUserPlanningId !== userPlanning.id)
                    ) {
                        if (
                            Utils.getTimeOrNull(userPlanning.begindate) < Utils.getTimeOrNull(enddate) &&
                            Utils.getTimeOrNull(userPlanning.enddate) > Utils.getTimeOrNull(begindate)
                        ) {
                            return true;
                        }
                    }
                    return false;
                })?.length === 0;

        const planningHasValid =
            planningList
                .map(pl => pl.planning_has)
                .flat()
                .filter(planningHas => {
                    if (
                        planningHas.driver_user_id === userId &&
                        (!oldUserPlanningId || oldUserPlanningId !== planningHas.id)
                    ) {
                        if (
                            Utils.getTimeOrNull(planningHas.begindate) < Utils.getTimeOrNull(enddate) &&
                            Utils.getTimeOrNull(planningHas.enddate) > Utils.getTimeOrNull(begindate)
                        ) {
                            return true;
                        }
                    }
                    return false;
                })?.length === 0;

        return userPlanningValid && planningHasValid;
    }
}
